import _toConsumableArray from "/builds/fathomio/NuaLang/nualang.com/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";

/* eslint-disable @typescript-eslint/ban-types */
var __rest = this && this.__rest || function (s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};

import { useMemo } from "react";
import { createUseCache } from "./makeStyles";
import { createUseCssAndCx } from "./cssAndCx";
import { assert } from "./tools/assert";
import { objectFromEntries } from "./tools/polyfills/Object.fromEntries";
import { objectKeys } from "./tools/objectKeys";
import { typeGuard } from "./tools/typeGuard";
import { getDependencyArrayRef } from "./tools/getDependencyArrayRef";
import { mergeClasses } from "./mergeClasses";
import { isSSR } from "./tools/isSSR";
export function createTss(params) {
  var useContext = params.useContext,
      usePlugin = params.usePlugin,
      cacheProvidedAtInception = params.cache;

  var _createUseCache = createUseCache({
    cacheProvidedAtInception: cacheProvidedAtInception
  }),
      useCache = _createUseCache.useCache;

  var _createUseCssAndCx = createUseCssAndCx({
    useCache: useCache
  }),
      useCssAndCx = _createUseCssAndCx.useCssAndCx;

  var usePluginDefault = function usePluginDefault(_ref) {
    var classes = _ref.classes,
        cx = _ref.cx,
        css = _ref.css;
    return {
      classes: classes,
      cx: cx,
      css: css
    };
  };

  var tss = createTss_internal({
    useContext: useContext,
    useCache: useCache,
    useCssAndCx: useCssAndCx,
    "usePlugin": usePlugin !== null && usePlugin !== void 0 ? usePlugin : usePluginDefault,
    "name": undefined,
    "doesUseNestedSelectors": false
  });
  return {
    tss: tss
  };
}
var counter = 0;
var nestedSelectorUsageTrackRecord = [];

function createTss_internal(params) {
  var useContext = params.useContext,
      useCache = params.useCache,
      useCssAndCx = params.useCssAndCx,
      usePlugin = params.usePlugin,
      name = params.name,
      doesUseNestedSelectors = params.doesUseNestedSelectors;
  return {
    "withParams": function withParams() {
      return createTss_internal(Object.assign({}, params));
    },
    "withName": function withName(nameOrWrappedName) {
      return createTss_internal(Object.assign(Object.assign({}, params), {
        "name": typeof nameOrWrappedName !== "object" ? nameOrWrappedName : Object.keys(nameOrWrappedName)[0]
      }));
    },
    "withNestedSelectors": function withNestedSelectors() {
      return createTss_internal(Object.assign(Object.assign({}, params), {
        "doesUseNestedSelectors": true
      }));
    },
    "create": function create(cssObjectByRuleNameOrGetCssObjectByRuleName) {
      // NOTE: Not isomorphic. Not guaranteed to be the same on client and server.
      // Do not attempt to 'simplify' the code without taking this fact into account.
      var idOfUseStyles = "x".concat(counter++);
      var getCssObjectByRuleName = typeof cssObjectByRuleNameOrGetCssObjectByRuleName === "function" ? cssObjectByRuleNameOrGetCssObjectByRuleName : function () {
        return cssObjectByRuleNameOrGetCssObjectByRuleName;
      };
      return function useStyles(params) {
        var _a, _b, _c;

        var _d = params !== null && params !== void 0 ? params : {},
            classesOverrides = _d.classesOverrides,
            paramsAndPluginParams = __rest(_d, ["classesOverrides"]);

        var context = useContext();

        var _useCssAndCx = useCssAndCx(),
            css = _useCssAndCx.css,
            cx = _useCssAndCx.cx;

        var cache = useCache();
        var classes = useMemo(function () {
          var refClassesCache = {}; // @ts-expect-error: Type safety non achievable.

          var cssObjectByRuleName = getCssObjectByRuleName(Object.assign(Object.assign(Object.assign({}, params), context), !doesUseNestedSelectors ? {} : {
            "classes": typeof Proxy === "undefined" ? {} : new Proxy({}, {
              "get": function get(_target, ruleName) {
                /* prettier-ignore */
                if (typeof ruleName === "symbol") {
                  assert(false);
                }

                if (isSSR) {
                  {
                    /* prettier-ignore */
                    var wrap = nestedSelectorUsageTrackRecord.find(function (wrap) {
                      return wrap.name === name && wrap.idOfUseStyles === idOfUseStyles;
                    });
                    /* prettier-ignore */

                    if (wrap === undefined) {
                      /* prettier-ignore */
                      wrap = {
                        name: name,
                        idOfUseStyles: idOfUseStyles,
                        "nestedSelectorRuleNames": new Set()
                      };
                      /* prettier-ignore */

                      nestedSelectorUsageTrackRecord.push(wrap);
                    }
                    /* prettier-ignore */


                    wrap.nestedSelectorRuleNames.add(ruleName);
                  }

                  if (
                  /* prettier-ignore */
                  nestedSelectorUsageTrackRecord.find(function (wrap) {
                    return wrap.name === name && wrap.idOfUseStyles !== idOfUseStyles && wrap.nestedSelectorRuleNames.has(ruleName);
                  }) !== undefined) {
                    throw new Error(["tss-react: Duplicate nested selector \"".concat(ruleName, "\" detected in ").concat(name === undefined ? "useStyles named \"".concat(name, "\"") : "anonymous useStyles function", "."), "In SSR setups, this may lead to CSS class name collisions, causing nested selectors to target elements outside of the intended scope.", "Solution: Ensure each useStyles using nested selectors has a unique name. Use tss.withName(\"UniqueName\").withNestedSelectors<...>()... to set a name."].join("\n"));
                  }
                }
                /* prettier-ignore */


                return refClassesCache[ruleName] = "".concat(cache.key, "-").concat(idOfUseStyles).concat(name !== undefined ? "-".concat(name) : "", "-").concat(ruleName, "-ref");
              }
            })
          }));
          var classes = objectFromEntries(objectKeys(cssObjectByRuleName).map(function (ruleName) {
            var cssObject = cssObjectByRuleName[ruleName];

            if (!cssObject.label) {
              cssObject.label = "".concat(name !== undefined ? "".concat(name, "-") : "").concat(ruleName);
            }

            return [ruleName, "".concat(css(cssObject)).concat(typeGuard(ruleName, ruleName in refClassesCache) ? " ".concat(refClassesCache[ruleName]) : "")];
          }));
          objectKeys(refClassesCache).forEach(function (ruleName) {
            if (ruleName in classes) {
              return;
            }

            classes[ruleName] = refClassesCache[ruleName];
          });
          return classes;
        }, [cache, css, cx, getDependencyArrayRef(params)].concat(_toConsumableArray(Object.values(context))));
        classes = useMemo(function () {
          return mergeClasses(classes, classesOverrides, cx);
        }, [classes, getDependencyArrayRef(classesOverrides), cx]); // @ts-expect-error: Type safety non achievable.

        var pluginResultWrap = usePlugin(Object.assign(Object.assign({
          classes: classes,
          css: css,
          cx: cx,
          idOfUseStyles: idOfUseStyles,
          name: name
        }, context), paramsAndPluginParams));
        return Object.assign({
          "classes": (_a = pluginResultWrap.classes) !== null && _a !== void 0 ? _a : classes,
          "css": (_b = pluginResultWrap.css) !== null && _b !== void 0 ? _b : css,
          "cx": (_c = pluginResultWrap.cx) !== null && _c !== void 0 ? _c : cx
        }, context);
      };
    }
  };
}